import {
  Avatar,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Divider,
} from '@mantine/core';
import {
  IconCalendar,
  IconCash,
  IconCheck,
  IconClock,
  IconMessage,
  IconTag,
  IconX,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { getBookingTopicLabel } from '../../../../utils/bookingTopic';
import { primaryColor } from '../../../../constants/theme';

export type BookingDecision = 'Accepted' | 'Rejected' | 'Cancelled' | null;

interface BookingDecisionCardProps {
  group: BookingResponseModel[];
  decision: BookingDecision;
  isLoading: boolean;
  profileId: string;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
}

const getUserInitials = (userName: string): string => {
  const parts = userName.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  testId?: string;
}

const InfoRow = ({ icon, label, value, testId }: InfoRowProps) => (
  <Group gap={12} align="flex-start" wrap="nowrap">
    <div style={{ marginTop: 2, flexShrink: 0 }}>{icon}</div>
    <Stack gap={2} align="flex-start">
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1} ta="left">
        {label}
      </Text>
      <Text size="sm" ta="left" data-testid={testId}>
        {value}
      </Text>
    </Stack>
  </Group>
);

const MESSAGE_MAX_CHARS = 200;

export const BookingDecisionCard = ({
  group,
  decision,
  isLoading,
  onAccept,
  onReject,
  onCancel,
}: BookingDecisionCardProps) => {
  const navigate = useNavigate();
  const first = group[0];
  const last = group[group.length - 1];

  const isExpired = new Date(first.startIso) < new Date();
  const totalRate = group.reduce((sum, b) => sum + b.rate, 0);
  const totalFee = group.reduce((sum, b) => sum + b.bookingFee, 0);
  const totalEarning = totalRate - totalFee;

  const formattedDate = format(new Date(first.startIso), 'EEEE, MMM d, yyyy');
  const formattedTime = (() => {
    const timeRange = `${format(new Date(first.startIso), 'hh:mm a')} - ${format(new Date(last.endIso), 'hh:mm a')}`;
    return group.length > 1 ? `${timeRange} (${group.length} sessions)` : timeRange;
  })();

  const topicLabel = getBookingTopicLabel(first) || 'Ask Me Anything';
  const costLabel =
    totalRate > 0
      ? `${first.currency} ${totalRate} total (${first.currency} ${totalFee.toFixed(2)} fee)`
      : 'Free';
  const earningsLabel = `${first.currency} ${totalEarning.toFixed(2)}`;

  const rawMessage = first.message?.trim() ?? '';
  const messageValue =
    rawMessage.length > MESSAGE_MAX_CHARS
      ? `${rawMessage.slice(0, MESSAGE_MAX_CHARS)}…`
      : rawMessage;

  const iconProps = { size: 18, color: 'gray' };

  return (
    <Paper withBorder radius="md" p="lg" bg="gray.0" style={{ height: '100%', width: '100%' }}>
      <Stack gap="md" justify="space-between" style={{ height: '100%' }}>
        <Stack gap="md">
          {/* User profile section */}
          <Stack align="center" gap={8}>
            <Avatar
              src={first.userProfilePicture || undefined}
              size={64}
              radius="50%"
              data-testid={`booking-decision-avatar-${first.id}`}
            >
              {getUserInitials(first.userName)}
            </Avatar>
            <Title order={4} fw={600} data-testid={`booking-decision-name-${first.id}`}>
              {first.userName}
            </Title>
            <Text
              size="sm"
              c={primaryColor}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/profile?professionalId=${first.userId}`)}
              data-testid={`booking-decision-view-profile-${first.id}`}
            >
              View Profile
            </Text>
          </Stack>

          <Divider />

          {/* Info rows */}
          <Stack gap="sm">
            <InfoRow
              icon={<IconCalendar {...iconProps} />}
              label="Date"
              value={formattedDate}
              testId={`booking-decision-date-${first.id}`}
            />
            <InfoRow
              icon={<IconClock {...iconProps} />}
              label="Time"
              value={formattedTime}
              testId={`booking-decision-time-${first.id}`}
            />
            <InfoRow
              icon={<IconTag {...iconProps} />}
              label="Topic"
              value={topicLabel}
              testId={`booking-decision-topic-${first.id}`}
            />
            {totalRate > 0 ? (
              <>
                <InfoRow
                  icon={<IconCash {...iconProps} />}
                  label="Cost Breakdown"
                  value={costLabel}
                  testId={`booking-decision-cost-${first.id}`}
                />
                <InfoRow
                  icon={<IconCash {...iconProps} />}
                  label="You Earn"
                  value={earningsLabel}
                  testId={`booking-decision-earnings-${first.id}`}
                />
              </>
            ) : (
              <InfoRow
                icon={<IconCash {...iconProps} />}
                label="Cost"
                value="Free"
                testId={`booking-decision-cost-${first.id}`}
              />
            )}
            <Group gap={12} align="flex-start" wrap="nowrap">
              <div style={{ marginTop: 2, flexShrink: 0 }}>
                <IconMessage size={18} color="gray" />
              </div>
              <Stack gap={2} align="flex-start">
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1} ta="left">
                  Message
                </Text>
                <Text
                  size="sm"
                  ta="left"
                  c={rawMessage ? undefined : 'dimmed'}
                  fs={rawMessage ? undefined : 'italic'}
                  data-testid={`booking-decision-message-${first.id}`}
                >
                  {rawMessage ? messageValue : 'No message provided'}
                </Text>
              </Stack>
            </Group>
          </Stack>
        </Stack>

        {/* Action area */}
        {decision === null && isExpired && (
          <Badge
            color="gray"
            variant="light"
            size="lg"
            radius="sm"
            fullWidth
            data-testid={`booking-decision-expired-badge-${first.id}`}
          >
            Expired
          </Badge>
        )}

        {decision === null && !isExpired && (
          <Group grow gap={8}>
            <Button
              variant="outline"
              color="red"
              leftSection={isLoading ? <Loader size={16} /> : <IconX size={18} />}
              disabled={isLoading}
              onClick={onReject}
              data-testid={`booking-decision-reject-${first.id}`}
            >
              Reject
            </Button>
            <Button
              color={primaryColor}
              leftSection={isLoading ? <Loader size={16} /> : <IconCheck size={18} />}
              disabled={isLoading}
              onClick={onAccept}
              data-testid={`booking-decision-accept-${first.id}`}
            >
              Accept
            </Button>
          </Group>
        )}

        {decision === 'Accepted' && (
          <Stack gap={8}>
            <Badge
              color="green"
              variant="light"
              size="lg"
              radius="sm"
              fullWidth
              leftSection={<IconCheck size={14} />}
              data-testid={`booking-decision-accepted-badge-${first.id}`}
            >
              Accepted
            </Badge>
            <Button
              variant="outline"
              color="red"
              leftSection={isLoading ? <Loader size={16} /> : undefined}
              disabled={isLoading || isExpired}
              onClick={onCancel}
              data-testid={`booking-decision-cancel-${first.id}`}
            >
              Cancel Booking
            </Button>
          </Stack>
        )}

        {decision === 'Rejected' && (
          <Badge
            color="red"
            variant="light"
            size="lg"
            radius="sm"
            fullWidth
            leftSection={<IconX size={14} />}
            data-testid={`booking-decision-rejected-badge-${first.id}`}
          >
            Rejected
          </Badge>
        )}

        {decision === 'Cancelled' && (
          <Badge
            color="orange"
            variant="light"
            size="lg"
            radius="sm"
            fullWidth
            data-testid={`booking-decision-cancelled-badge-${first.id}`}
          >
            Cancelled
          </Badge>
        )}
      </Stack>
    </Paper>
  );
};
